import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@app/classes';
import { DeprecatedModalFormComponent } from '@app/components';
import { FeatureFlag } from '@app/enums';
import { TimeClockEntry } from '@app/models/time-tracking/time-clock-entry.model';
import { TimesheetEntry } from '@app/models/time-tracking/timesheet-entry.model';
import { TimeTrackingSettingsService } from '@app/modules/time-tracking/services/time-tracking-settings.service';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { BaseForm } from '@forms/base.form';
import { Announcement } from '@models/company/announcement.model';
import { Employee } from '@models/employee/employee.model';
import moment from 'moment';

@Component({
    styleUrls: ['./dashboard.style.scss'],
    templateUrl: './dashboard.view.html',
})
export class DashboardView implements OnInit {
    timeOffPermission = 'accessTimeOff';
    managerPermission = 'manageDirectReports';
    canBookTimeOffFromDashboard = false;
    isEmployee = false;
    canSendAnnouncement = false;
    showTimeTrackingWidget = false;
    showTimeClockWidget = false;
    showTimeClock = false;

    /**
     * timeTrackingMinutes is used to display the time tracking widget's time
     * For users without time tracking, the widget will not be displayed.
     */
    timeTrackingMinutes = {
        yesterday: 0,
        today: 0,
    };

    timeClockEntries: TimeClockEntry[] = [];

    constructor(
        public auth: AuthService,
        private notify: NotifyService,
        private featureService: FeatureService,
        private timeTrackingSettingsService: TimeTrackingSettingsService,
        private router: Router
    ) {}

    async ngOnInit(): Promise<void> {
        this.showTimeClock =
            (await this.featureService.has(FeatureFlag.timeClockTimer)) &&
            (await this.timeTrackingSettingsService.get()).timeTrackingMethod === 'start_end_time';

        // Should never happen, this is an authenticated view
        if (!this.auth.employee || !this.auth.company) {
            return;
        }

        this.isEmployee = !this.auth.isAdmin();
        this.canSendAnnouncement = this.auth.can('create.companyNotificationAnnouncement');

        const isNotAdminOrManager = !this.auth.isAdmin() && this.auth.cant(this.managerPermission);

        const employee: Employee = await Employee.param('company', this.auth.company.id)
            .with(['activeTimeOffPolicies', 'activeModuleAssignments.module'])
            .find(this.auth.employee.id);

        // Get assigned time off policies to the auth employee
        const hasTimeOffPolicyAssigned = Boolean(employee.activeTimeOffPolicies?.length);

        // If the user is not an admin/manager, and has access to time off,
        // we let them book time off from dashboard.
        this.canBookTimeOffFromDashboard =
            isNotAdminOrManager &&
            this.auth.can(this.timeOffPermission) &&
            this.auth.company.modules.some((m) => m.name === Platform.modules.timeOff) &&
            hasTimeOffPolicyAssigned;

        // Time tracking
        const hasTimeTracking = employee.hasModule(Platform.modules.timeTracking);

        const willShowTimeTrackingWidget = hasTimeTracking && isNotAdminOrManager && !this.showTimeClock;
        const willShowTimeClock = hasTimeTracking && isNotAdminOrManager && this.showTimeClock;

        if (willShowTimeTrackingWidget) {
            this.loadTimeTrackingData();
            this.showTimeTrackingWidget = true;
        }

        if (willShowTimeClock) {
            await this.loadTimeClockData();
            this.showTimeClockWidget = true;
        }
    }

    getFirstName(): string {
        return this.auth.user?.firstName || '';
    }

    sendAnnouncement(announcement: Announcement, form: BaseForm): void {
        announcement
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.notify.success('dashboard.announcementSent');
                form.reset();
            })
            .catch((err) => this.notify.error(err || 'dashboard.sendAnnouncementError'));
    }

    getTimeGreeting(): string {
        const today: Date = new Date();
        const currentHour: number = today.getHours();

        if (currentHour < 12) {
            return 'dashboard.goodMorning';
        } else if (currentHour < 18) {
            return 'dashboard.goodAfternoon';
        } else {
            return 'dashboard.goodEvening';
        }
    }

    async createAnnouncement(announcementModal: DeprecatedModalFormComponent): Promise<void> {
        const hasCompanyAnnouncementsFeatureFlag = await this.featureService.has(FeatureFlag.companyAnnouncements);

        if (hasCompanyAnnouncementsFeatureFlag) {
            this.router.navigate(['dashboard', 'create-announcement']);

            return;
        }

        announcementModal.show();
    }
    async loadTimeClockData(): Promise<void> {
        const today = moment(new Date()).format('YYYY-MM-DD');
        const yesterday = moment(new Date()).subtract(1, 'days').format('YYYY-MM-DD');

        const [timesheetEntries] = await TimesheetEntry.where('employee_id', this.auth.employee.id)
            .where('worked_on', {
                from: yesterday,
                to: today,
            })
            .with('timeClockEntry')
            .all();

        this.timeClockEntries = timesheetEntries.filter((t) => t.timeClockEntry).map((t) => t.timeClockEntry!);

        // We still need the minutes for today for the time clock widget
        this.timeTrackingMinutes.today = timesheetEntries
            .filter((t) => t.workedOn === today)
            .reduce((acc, t) => acc + t.minutes, 0);
    }

    private async loadTimeTrackingData(): Promise<void> {
        const today = moment(new Date()).format('YYYY-MM-DD');
        const yesterday = moment(new Date()).subtract(1, 'days').format('YYYY-MM-DD');

        const [timesheetEntries] = await TimesheetEntry.where('employee_id', this.auth.employee.id)
            .where('worked_on', {
                from: yesterday,
                to: today,
            })
            .all();

        this.timeTrackingMinutes.yesterday = timesheetEntries
            .filter((t) => t.workedOn === yesterday)
            .reduce((acc, t) => acc + t.minutes, 0);

        this.timeTrackingMinutes.today = timesheetEntries
            .filter((t) => t.workedOn === today)
            .reduce((acc, t) => acc + t.minutes, 0);
    }
}
